import { useEffect, useRef } from 'react'

/**
 * Hook to show browser's native exit confirmation dialog
 * when user tries to close tab, refresh, or navigate away.
 *
 * Used during the loan journey to prevent accidental navigation
 * and remind customers they may lose entered information.
 *
 * Handles:
 * - Browser close/refresh (beforeunload event)
 * - Browser back/forward buttons (popstate event + history manipulation)
 */
export const useExitConfirmation = () => {
  const isNavigatingRef = useRef(false)

  useEffect(() => {
    // Handle browser close/refresh
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = 'Changes you made may not be saved.'
      return event.returnValue
    }

    // Push a dummy state to detect back button
    window.history.pushState(null, '', window.location.href)

    // Handle browser back/forward buttons
    const handlePopState = () => {
      if (isNavigatingRef.current) {
        return
      }

      const confirmLeave = window.confirm('Are you sure? Changes that you made may not be saved.')
      if (confirmLeave) {
        isNavigatingRef.current = true
        window.history.back()
      } else {
        // Stay on current page - push state again to reset
        window.history.pushState(null, '', window.location.href)
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    window.addEventListener('popstate', handlePopState)

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
      window.removeEventListener('popstate', handlePopState)
    }
  }, [])
}
